import React, { useMemo, useState } from "react";
import { useTheme } from "styled-components";
import { UsagePlan } from "pages/subscription/SubscriptionStyled";
import { WithTooltip } from "components/blocks/WithTooltip";
import { FaInfoCircle } from "react-icons/fa";
import {
  BILLING_CYCLE,
  SUBSCRIPTION_STATUS,
} from "api/interfaces/subscription.interface";
import { formatNumber } from "utils/functions";
import { ReactComponent as RecorderSVG } from "assets/icons/sidenav/recorder.svg";
import { ReactComponent as OrganizationSVG } from "assets/icons/sidenav/account.svg";
import { FaRegHardDrive } from "react-icons/fa6";
import { RiMoneyDollarCircleLine } from "react-icons/ri";
import {
  FiActivity,
  FiAlertTriangle,
  FiBarChart2,
  FiFileText,
} from "react-icons/fi";
import {
  PLAN_TYPE,
  StatCardResultDto,
  UsageStorageList,
} from "api/interfaces/planInterface.interface";
import { ProfileAccountInfo } from "api/interfaces/accountInterface.interface";
import { useAppSelector } from "redux/hooks";
import moment from "moment";
import { IoCardOutline } from "react-icons/io5";
import { isYearlyBillingCycle } from "utils/priceUtils";
import { getActivateStatus } from "utils/SubscriptionUtil";
import ExpiredDate from "pages/subscription/modal/common/ExpiredDate";
import BreakdownModal from "./modal/BreakdownModal";
import ChannelBreakdownContent, {
  CHANNEL_BREAKDOWN_SUBTITLE,
  CHANNEL_BREAKDOWN_TITLE,
} from "./modal/ChannelBreakdownContent";
import StorageBreakdownContent, {
  STORAGE_BREAKDOWN_SUBTITLE,
  STORAGE_BREAKDOWN_TITLE,
} from "./modal/StorageBreakdownContent";
import EstimateCostBreakdownContent, {
  ESTIMATE_COST_BREAKDOWN_SUBTITLE,
  ESTIMATE_COST_BREAKDOWN_TITLE,
} from "./modal/EstimateCostBreakdownContent";
const BREAKDOWN_CONTENT: Record<
  string,
  {
    title: string;
    subTitle: string;
    render: (row: StatCardResultDto) => React.ReactNode;
  }
> = {
  activeChannels: {
    title: CHANNEL_BREAKDOWN_TITLE,
    subTitle: CHANNEL_BREAKDOWN_SUBTITLE,
    render: (row) => <ChannelBreakdownContent summary={row} />,
  },
  activeStorage: {
    title: STORAGE_BREAKDOWN_TITLE,
    subTitle: STORAGE_BREAKDOWN_SUBTITLE,
    render: (row) => <StorageBreakdownContent summary={row} />,
  },
  estimateCost: {
    title: ESTIMATE_COST_BREAKDOWN_TITLE,
    subTitle: ESTIMATE_COST_BREAKDOWN_SUBTITLE,
    render: (row) => <EstimateCostBreakdownContent summary={row} />,
  },
};

type PlanSummaryCard = {
  key: string;
  title: string;
  tooltipTitle?: string;
  value: (row: StatCardResultDto) => React.ReactNode;
  descriptionRender: (row: StatCardResultDto) => React.ReactNode;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement> & { title?: string }>;
  tooltip?: string | ((row: StatCardResultDto) => string);
  tooltipExample?: string | ((row: StatCardResultDto) => React.ReactNode);
  breakdownLabel?: string;
  valueClassName?: (row: StatCardResultDto) => string | undefined;
  iconClassName?: (row: StatCardResultDto) => string | undefined;
  iconStatus?: (row: StatCardResultDto) => string | undefined;
};

type Props = {
  cycle: BILLING_CYCLE;
  summary: StatCardResultDto;
  summaryType: PLAN_TYPE;
  storages?: UsageStorageList[];
};

export default function PlanSummaryCards(props: Props) {
  const theme = useTheme();
  const { planActiveDate }: ProfileAccountInfo = useAppSelector(
    (state) => state.accountSettings
  );
  const isStorage = props.summaryType === PLAN_TYPE.STORAGE;

  const [breakdownModalKey, setBreakdownModalKey] = useState<string | null>(
    null
  );

  const monthsLeftInYear = useMemo(() => {
    if (!planActiveDate) return 0;
    const start = moment(planActiveDate);
    if (!start.isValid()) return 0;
    const end = start.clone().add(1, "year");
    const diff = end.diff(moment(), "months");
    return diff > 0 ? diff : 0;
  }, [planActiveDate]);

  const monthsElapsedInYear = useMemo(() => {
    if (!planActiveDate) return 0;
    const start = moment(planActiveDate);
    if (!start.isValid()) return 0;
    const diff = moment().diff(start, "months");
    return diff > 0 ? diff : 0;
  }, [planActiveDate]);

  const monthlyCardList: PlanSummaryCard[] = [
    {
      key: "endUsers",
      title: "Active End Users",
      value: (row: StatCardResultDto) =>
        formatNumber(row.activeUser?.endUser ?? 0),
      descriptionRender: (row: StatCardResultDto) => (
        <>
          Inactive <b>{formatNumber(row.activeUser?.inActivate ?? 0)}</b>
        </>
      ),
      icon: OrganizationSVG,
      tooltip:
        "Count of end-user organizations that are currently switched on this month. Each activated organization contributes its channels to the monthly usage.",
    },
    {
      key: "activeChannels",
      title: "Avg Daily Channels",
      value: (row: StatCardResultDto) =>
        formatNumber(row.activeChannels?.channel ?? 0),
      descriptionRender: (row: StatCardResultDto) => {
        if (!isYearlyBillingCycle(props.cycle)) {
          return (
            <>
              {formatNumber(row.activeChannels?.channel ?? 0)} X $
              {formatNumber(row.activeChannels?.channelPrice ?? 0)}/ch/month ={" "}
              <b>
                $
                {formatNumber(
                  (row.activeChannels?.channelPrice ?? 0) *
                    (row.activeChannels?.channel ?? 0)
                )}
              </b>
            </>
          );
        }
      },
      icon: RecorderSVG,
      tooltip:
        "We add up the active channels for every day of the month, then divide by the number of days. This smooths out mid-month activations and deactivations, so you only pay for the average daily usage.",
      breakdownLabel: isYearlyBillingCycle(props.cycle)
        ? undefined
        : "View calculation breakdown",
    },
    {
      key: "activeStorage",
      title: "Pro-rated Storage",
      value: (row: StatCardResultDto) =>
        `${row.activeStorage?.storage ?? 0} TB`,
      descriptionRender: (row: StatCardResultDto) => {
        const storage = row.activeStorage?.storage ?? 0;
        return (
          <>
            {storage} X ${formatNumber(row.activeStorage?.storagePrice ?? 0)}
            /TB/month ={" "}
            <b>
              ${formatNumber(storage * (row.activeStorage?.storagePrice ?? 0))}
            </b>
          </>
        );
      },
      icon: FaRegHardDrive,
      tooltip:
        "For each organization, we multiply the allocated TB by the number of days it was active this month, then divide by the total days in the month. This gives a prorated TB-month value, so you only pay for the time storage was used.",
      breakdownLabel: "View storage calculation",
    },
    {
      key: "estimateCost",
      title: "Est. Monthly Cost",
      value: (row: StatCardResultDto) =>
        `$${formatNumber(row.estimateCost?.cost ?? 0)}`,
      descriptionRender: (row: StatCardResultDto) => (
        <>Based on current cycle usage</>
      ),
      icon: RiMoneyDollarCircleLine,
      tooltip: (row: StatCardResultDto) =>
        `Total estimated bill = (Average daily channels × $${formatNumber(
          row.activeChannels?.channelPrice ?? 0
        )} per channel per month) + (Pro-rated TB-months × $${formatNumber(
          row.activeStorage?.storagePrice ?? 0
        )} per TB per month). This is the amount we would invoice based on current usage.`,
      breakdownLabel: "View calculation breakdown",
    },
  ];

  const getYearlyCardList = (): PlanSummaryCard[] => [
    {
      key: "yearlyCredits",
      title: isStorage ? "Storage Credits" : "Yearly Credits",
      value: (row: StatCardResultDto) =>
        formatNumber(row.yearlyCredit?.amount ?? 0),
      descriptionRender: (row: StatCardResultDto) =>
        isStorage
          ? `TB-month credits`
          : `${formatNumber(row.yearlyCredit?.contract ?? 0)} channels x ${
              row.yearlyCredit?.period === -1
                ? "3 days"
                : `${row.yearlyCredit?.period * 12} months`
            }`,
      icon: IoCardOutline,
      tooltip: isStorage
        ? "Total prepaid storage credits measured in TB-months. One TB-month equals one terabyte allocated for one full month."
        : "Total channel credits purchased upfront for the year. Each prepaid channel gives one credit per month for 12 months.",
      tooltipExample: (row: StatCardResultDto) => {
        const contract = formatNumber(row.yearlyCredit?.contract ?? 0);
        const amount = formatNumber(row.yearlyCredit?.amount ?? 0);
        return isStorage
          ? `Example: ${(row.yearlyCredit?.amount ?? 0).toFixed(
              2
            )} TB-months prepaid for the year`
          : `Example: ${contract} channels × ${
              row.yearlyCredit?.period === -1
                ? "3 days"
                : `${row.yearlyCredit?.period * 12} months`
            } = ${amount} credits`;
      },
    },
    {
      key: "used",
      title: "Credits Used",
      value: (row: StatCardResultDto) =>
        formatNumber(row.usedCredit?.used ?? 0),
      descriptionRender: (row: StatCardResultDto) => {
        if (isStorage) {
          return "up to current date";
        }
        const start = row.usedCredit?.start
          ? moment(row.usedCredit.start).format("M/D/YY")
          : "";
        const end = row.usedCredit?.end
          ? moment(row.usedCredit.end).format("M/D/YY")
          : "";
        return start && end ? `${start} – ${end}` : "";
      },
      icon: FiBarChart2,
      tooltip: isStorage
        ? "Total TB-month credits consumed so far. Based on your allocated TB multiplied by the months elapsed, plus any mid-cycle allocation changes."
        : "Sum of weighted average channel usage each month. Only months with usage are shown in the breakdown.",
      tooltipExample: isStorage
        ? (row: StatCardResultDto) => {
            const contract = row.yearlyCredit?.contract ?? 0;
            const used = row.usedCredit?.used ?? 0;
            const baseUsage = contract * monthsElapsedInYear;
            return (
              <>
                Base usage:
                <br />
                {contract.toFixed(2)} TB × {monthsElapsedInYear} mo ={" "}
                {baseUsage.toFixed(2)}
                <br />
                Total used:
                <br />
                {used.toFixed(2)}
              </>
            );
          }
        : undefined,
    },
    {
      key: "remaining",
      title: "Credits Remaining",
      value: (row: StatCardResultDto) =>
        `${formatNumber(row.leftCredit?.remains ?? 0)}`,
      descriptionRender: (row: StatCardResultDto) =>
        `${formatNumber(row.leftCredit?.utilization ?? 0)}% utilized`,
      icon: RiMoneyDollarCircleLine,
      tooltip:
        "Credits left after subtracting used credits from your yearly total. Turns red when 80% or more of your credits have been used.",
      tooltipExample: (row: StatCardResultDto) => {
        const amount = row.yearlyCredit?.amount ?? 0;
        const used = row.usedCredit?.used ?? 0;
        const remaining = Math.max(amount - used, 0);
        return `Example: ${amount} credits - ${used} used = ${remaining} remaining`;
      },
      valueClassName: (row: StatCardResultDto) =>
        (row.leftCredit?.utilization ?? 0) >= 80 ? "danger" : undefined,
    },
    {
      key: "burnRate",
      title: "Avg Burn Rate",
      value: (row: StatCardResultDto) => (row.burnRate?.rate ?? 0).toFixed(2),
      descriptionRender: () =>
        isStorage ? "TB-months / month" : "credits / month",
      icon: FiActivity,
      tooltip:
        "Average channel credits consumed per month, based only on months with recorded usage. This shows how quickly your prepaid credits are being spent.",
      tooltipExample: (row: StatCardResultDto) =>
        `Example: ${(row.burnRate?.rate ?? 0).toFixed(
          2
        )} credits ÷ months with usage = ${(row.burnRate?.rate ?? 0).toFixed(
          2
        )} credits/month`,
    },
    {
      key: "expire",
      title: isStorage ? "Projection" : "Projected Date Credits Expire",
      tooltipTitle: isStorage ? "Storage Projection" : undefined,
      iconStatus: (row: StatCardResultDto) => {
        if (isStorage) {
          return undefined;
        }
        const expireDate = row.expireCredit?.expireDate;
        if (!expireDate) return undefined;
        return getActivateStatus({ expireDate });
      },
      value: (row: StatCardResultDto) => {
        if (isStorage) {
          const rate = row.expireCredit?.rate ?? 0;
          return rate < 0
            ? `Over by ${Math.abs(rate).toFixed(2)}` // remain - 사용 * 남은달수
            : rate.toFixed(2);
        }
        const expireDate = row.expireCredit?.expireDate;
        if (!expireDate) return "-";
        const status = getActivateStatus({ expireDate });
        return (
          <ExpiredDate
            status={status}
            expiredDate={expireDate}
            hideIcon={true}
          />
        );
      },
      descriptionRender: (row: StatCardResultDto) => {
        if (isStorage) {
          return "credits left at year end";
        }
        const expireDate = row.expireCredit?.expireDate;
        const status = expireDate
          ? getActivateStatus({ expireDate })
          : SUBSCRIPTION_STATUS.NONE;
        return status === SUBSCRIPTION_STATUS.ACTIVATED ||
          status === SUBSCRIPTION_STATUS.NONE
          ? "at current burn rate"
          : "credits exhausted";
      },
      icon: FiAlertTriangle,
      iconClassName: (row: StatCardResultDto) => {
        if (isStorage) {
          return row.expireCredit?.rate < 0 ? "danger" : undefined;
        }
        return undefined;
      },
      tooltip: isStorage
        ? "Estimated TB-month credits remaining at year end if your current storage burn rate continues. A negative value means you are projected to exceed your prepaid storage credits and be billed monthly for overages."
        : "Estimated date when your prepaid channel credits will run out at the current average burn rate.",
      tooltipExample: isStorage
        ? (row: StatCardResultDto) => {
            const remaining = row.leftCredit?.remains ?? 0;
            const rate = row.burnRate?.rate ?? 0;
            const remainDate = row.expireCredit?.remainDate ?? monthsLeftInYear;
            const projected = row.expireCredit?.rate ?? 0;
            return `Example: ${remaining.toFixed(
              2
            )} remaining - (${rate.toFixed(
              2
            )} × ${remainDate} months left) = ${Math.abs(projected).toFixed(
              2
            )} TB-months at year end`;
          }
        : (row: StatCardResultDto) => {
            const expireDate = row.expireCredit?.expireDate;
            if (!expireDate) return undefined;
            const formatted = moment(expireDate).format("M/D/YY");
            const isPast = moment(expireDate).isBefore(moment(), "day");
            return isPast
              ? `Example: Credits are already exhausted as of ${formatted}.`
              : `Example: Credits will be exhausted as of ${formatted}.`;
          },
      valueClassName: (row: StatCardResultDto) =>
        row.expireCredit?.rate < 0 ? "danger" : undefined,
    },
    { ...monthlyCardList.find((card) => card.key === "endUsers")! },
    { ...monthlyCardList.find((card) => card.key === "activeChannels")! },
  ];

  const summaryCards: PlanSummaryCard[] = useMemo(() => {
    if (isYearlyBillingCycle(props.cycle)) {
      return getYearlyCardList().filter((card) => {
        if (isStorage) {
          return card.key !== "endUsers" && card.key !== "activeChannels";
        }
        return true;
      });
    }
    return monthlyCardList;
  }, [props.cycle, props.summaryType, monthsLeftInYear, monthsElapsedInYear]);

  return (
    <UsagePlan.CardWrapper
      columns={
        isStorage ? summaryCards.length : Math.min(summaryCards.length, 4)
      }
    >
      {summaryCards.map((card, index) => {
        const cardBody = (
          <>
            <UsagePlan.CardIcon
              className={`${card.key}-icon ${
                card.iconClassName?.(props.summary) ?? ""
              }`}
              $status={card.iconStatus?.(props.summary)}
            >
              <card.icon />
            </UsagePlan.CardIcon>
            <UsagePlan.CardContent>
              <div className="title">
                {card.title}
                {card.tooltip && <FaInfoCircle />}
              </div>
              <div
                className={`value ${
                  card.valueClassName?.(props.summary) ?? ""
                }`}
              >
                {card.value(props.summary)}
              </div>
              <div className="description">
                {card.descriptionRender(props.summary)}
              </div>
              {card.breakdownLabel && BREAKDOWN_CONTENT[card.key] && (
                <div
                  className="breakdown-link"
                  onClick={() => setBreakdownModalKey(card.key)}
                >
                  <FiFileText size={12} />
                  <span>{card.breakdownLabel}</span>
                </div>
              )}
            </UsagePlan.CardContent>
          </>
        );

        return (
          <UsagePlan.Card
            key={card.key}
            className={
              card.key === "activeStorage" && isStorage
                ? "storage-empty-card"
                : undefined
            }
          >
            {card.tooltip ? (
              <WithTooltip
                tooltip={
                  <UsagePlan.TooltipCard>
                    <div className="tooltip-title">
                      {card.tooltipTitle ?? card.title}
                    </div>
                    <div className="tooltip-desc">
                      {typeof card.tooltip === "function"
                        ? card.tooltip(props.summary)
                        : card.tooltip}
                    </div>
                    {card.tooltipExample && (
                      <div className="tooltip-example">
                        {typeof card.tooltipExample === "function"
                          ? card.tooltipExample(props.summary)
                          : card.tooltipExample}
                      </div>
                    )}
                  </UsagePlan.TooltipCard>
                }
                style={{
                  width: 300,
                  padding: 0,
                  borderRadius: 10,
                  backgroundColor: theme.COLORS.BACKGROUND,
                  border: `1px solid ${theme.COLORS.LIGHTGRAYBORDER}`,
                  boxShadow: "0 10px 30px rgba(0, 0, 0, 0.35)",
                }}
                right={index === summaryCards.length - 1}
                hideArrow
              >
                {cardBody}
              </WithTooltip>
            ) : (
              cardBody
            )}
          </UsagePlan.Card>
        );
      })}
      {breakdownModalKey && BREAKDOWN_CONTENT[breakdownModalKey] && (
        <BreakdownModal
          title={BREAKDOWN_CONTENT[breakdownModalKey].title}
          subTitle={BREAKDOWN_CONTENT[breakdownModalKey].subTitle}
          onModal={() => setBreakdownModalKey(null)}
        >
          {BREAKDOWN_CONTENT[breakdownModalKey].render(props.summary)}
        </BreakdownModal>
      )}
    </UsagePlan.CardWrapper>
  );
}
